/**
 * Delete folder dialog — confirmation step before issuing the destructive
 * call. Stage 2 surfaces the phase-2 toast; the real delete API is in Stage 7.
 */
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useWorkspaceData } from "@/hooks/use-workspace-data";

export interface DeleteFolderDialogProps {
  folderId: string | null;
  folderName?: string;
  projectCount?: number;
  onOpenChange: (open: boolean) => void;
}

export function DeleteFolderDialog({
  folderId,
  folderName,
  projectCount = 0,
  onOpenChange,
}: DeleteFolderDialogProps): JSX.Element {
  const deleteFolder = useWorkspaceData((s) => s.deleteFolder);

  async function confirm(): Promise<void> {
    if (!folderId) return;
    try {
      await deleteFolder({ id: folderId });
      onOpenChange(false);
    } catch {
      // phase_2 toast already shown
    }
  }

  return (
    <Dialog open={folderId !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete folder?</DialogTitle>
          <DialogDescription>
            This will permanently remove{" "}
            <span className="font-medium text-foreground">{folderName ?? "this folder"}</span>
            {projectCount > 0 ? (
              <>
                {" "}
                and the {projectCount} project{projectCount === 1 ? "" : "s"} it contains.
              </>
            ) : (
              "."
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => void confirm()}>
            Delete folder
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
